import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getElectionStats } from '../data/votingRepository';
import type { ElectionStats, RoundStats } from '../types';
import { RESULT_LABEL } from '../utils/rounds';
import { exportStatsExcel } from '../utils/exportStats';
import { fillMinutes, formatDuration, nonVoters, pct } from '../utils/stats';
import { votingErrorMessage } from '../utils/votingErrors';

function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function MinuteChart({ round }: { round: RoundStats }) {
  const bars = fillMinutes(round.per_minute);
  if (bars.length === 0) return <p className="text-sm text-muted-foreground">Sin votos en esta ronda.</p>;
  const max = Math.max(...bars.map((b) => b.n));
  return (
    <div>
      <div className="flex h-28 items-end gap-0.5" role="img" aria-label="Votos por minuto">
        {bars.map((b) => (
          <div
            key={b.t}
            className="min-w-[3px] flex-1 rounded-t bg-primary/70"
            style={{ height: `${Math.max(4, (b.n / max) * 100)}%` }}
            title={`${new Date(b.t).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}: ${b.n} voto(s)`}
          />
        ))}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Votos por minuto · máximo {max} · {new Date(bars[0].t).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })} a{' '}
        {new Date(bars[bars.length - 1].t).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}
      </p>
    </div>
  );
}

function RoundRow({ r, open, onToggle }: { r: RoundStats; open: boolean; onToggle: () => void }) {
  const turnout = pct(r.votes_cast, r.eligible);
  return (
    <>
      <tr className="cursor-pointer border-b hover:bg-muted/40" onClick={onToggle}>
        <td className="p-2 font-medium">{r.position_title ?? '—'} · R{r.round_number}</td>
        <td className="px-2">
          {r.status === 'OPEN' ? <Badge className="bg-green-600 hover:bg-green-600">Abierta</Badge>
            : r.status === 'DRAFT' ? <Badge variant="secondary">Borrador</Badge>
            : <Badge variant={r.result === 'WINNER' ? 'default' : 'outline'}>{r.result ? RESULT_LABEL[r.result] : 'Cerrada'}</Badge>}
        </td>
        <td className="px-2 text-right tabular-nums">{r.eligible ?? '—'}</td>
        <td className="px-2 text-right tabular-nums">{r.votes_cast}</td>
        <td className="px-2 text-right tabular-nums">{turnout === null ? '—' : `${turnout} %`}</td>
        <td className="px-2 text-right tabular-nums">{r.valid}</td>
        <td className="px-2 text-right tabular-nums">{r.blank}</td>
        <td className="px-2 text-right tabular-nums">{r.status === 'DRAFT' ? '—' : nonVoters(r)}</td>
        <td className="px-2 text-right tabular-nums">{r.by_channel.KIOSK ?? 0}</td>
        <td className="px-2 text-right tabular-nums">{r.by_channel.PUBLIC_LINK ?? 0}</td>
        <td className="px-2 text-right tabular-nums">{r.by_channel.ACCOUNT ?? 0}</td>
        <td className="px-2 text-right tabular-nums">{r.overrides}</td>
        <td className="whitespace-nowrap px-2 text-right">{formatDuration(r.duration_seconds)}{r.close_reason === 'TIMER' ? ' (tiempo)' : ''}</td>
      </tr>
      {open && (
        <tr className="border-b bg-muted/20">
          <td colSpan={13} className="p-3"><MinuteChart round={r} /></td>
        </tr>
      )}
    </>
  );
}

/** Election page tab: numbers about how the voting went (never for whom: that is in the results) */
export function StatsPanel({ electionId, electionName }: { electionId: string; electionName: string }) {
  const [openRound, setOpenRound] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['voting', 'stats', electionId],
    queryFn: () => getElectionStats(electionId),
    refetchInterval: (q) => ((q.state.data as ElectionStats | undefined)?.rounds.some((r) => r.status === 'OPEN') ? 4000 : 20000),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
  if (error || !data) return <p className="text-sm text-destructive">{votingErrorMessage(error)}</p>;

  const e = data.election;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Resumen de la elección</CardTitle>
            <CardDescription>Se actualiza sola mientras hay una ronda abierta.</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={exporting}
            onClick={async () => {
              setExporting(true);
              try { await exportStatsExcel(electionName, data); } finally { setExporting(false); }
            }}
          >
            {exporting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Download className="mr-2 size-4" />} Exportar a Excel
          </Button>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Cargos cubiertos" value={`${e.positions_filled} / ${e.positions}`} />
          <Stat label="Rondas" value={e.rounds} hint={`${e.rounds_closed} cerrada(s)`} />
          <Stat label="Personas que votaron" value={e.distinct_voters} hint={`Ministros ${e.by_kind.MINISTER ?? 0} · invitados ${e.by_kind.GUEST ?? 0} · manuales ${e.by_kind.MANUAL ?? 0}`} />
          <Stat label="Votos emitidos" value={e.votes} hint={`Kiosco ${e.by_channel.KIOSK ?? 0} · teléfono ${e.by_channel.PUBLIC_LINK ?? 0} · cuenta ${e.by_channel.ACCOUNT ?? 0}`} />
          <Stat label="Votos corregidos" value={e.overrides} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Por ronda</CardTitle>
          <CardDescription>Toque una fila para ver los votos por minuto. "No votaron" cuenta como blanco cuando el porcentaje se calcula sobre los habilitados.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {data.rounds.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aún no hay rondas.</p>
          ) : (
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="p-2">Cargo · ronda</th><th className="px-2">Estado</th>
                  <th className="px-2 text-right">Habilitados</th><th className="px-2 text-right">Votaron</th><th className="px-2 text-right">Particip.</th>
                  <th className="px-2 text-right">Válidos</th><th className="px-2 text-right">Blancos</th><th className="px-2 text-right">No votaron</th>
                  <th className="px-2 text-right">Kiosco</th><th className="px-2 text-right">Teléfono</th><th className="px-2 text-right">Cuenta</th>
                  <th className="px-2 text-right">Corregidos</th><th className="px-2 text-right">Duración</th>
                </tr>
              </thead>
              <tbody>
                {data.rounds.map((r) => (
                  <RoundRow key={r.race_id} r={r} open={openRound === r.race_id} onToggle={() => setOpenRound(openRound === r.race_id ? null : r.race_id)} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
